import type { FC } from "react";

import type { SourceControlRepository } from "../../types";

import { EmptyState } from "../common/EmptyState";
import { LoadingSkeleton } from "../common/LoadingState";

interface RepositoryChatRepositoryPickerProps {
  filteredRepositories: SourceControlRepository[];
  loadingRepositories: boolean;
  onToggleRepository: (repository: SourceControlRepository) => void;
  selectedProviderId: null | string;
  selectedRepositorySlugs: string[];
}

export const RepositoryChatRepositoryPicker: FC<
  RepositoryChatRepositoryPickerProps
> = ({
  filteredRepositories,
  loadingRepositories,
  onToggleRepository,
  selectedProviderId,
  selectedRepositorySlugs,
}) => {
  const content = renderRepositoryPickerContent(
    filteredRepositories,
    loadingRepositories,
    onToggleRepository,
    selectedProviderId,
    selectedRepositorySlugs,
  );

  return (
    <div className="max-h-64 space-y-2 overflow-y-auto pr-1">{content}</div>
  );
};

const RepositoryPickerRow: FC<{
  isLockedOut: boolean;
  isSelected: boolean;
  onClick: () => void;
  repository: SourceControlRepository;
}> = ({ isLockedOut, isSelected, onClick, repository }) => (
  <button
    className={`w-full border px-3 py-3 text-left transition-colors ${
      isSelected
        ? "border-emerald-500 bg-emerald-50 text-slate-950 dark:bg-emerald-500/10 dark:text-slate-100"
        : "border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white dark:border-slate-700 dark:bg-slate-900/60 dark:hover:bg-slate-900"
    } ${isLockedOut ? "cursor-not-allowed opacity-45" : ""}`}
    disabled={isLockedOut}
    onClick={onClick}
    type="button"
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="font-mono text-[11px] font-bold text-slate-900 dark:text-slate-100">
          {repository.remote_slug}
        </div>
        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {repository.default_branch
            ? `Default branch: ${repository.default_branch}`
            : "No default branch recorded"}
        </div>
      </div>
      <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-400">
        {isSelected ? "Selected" : "Add"}
      </span>
    </div>
  </button>
);

function renderRepositoryPickerContent(
  filteredRepositories: SourceControlRepository[],
  loadingRepositories: boolean,
  onToggleRepository: (repository: SourceControlRepository) => void,
  selectedProviderId: null | string,
  selectedRepositorySlugs: string[],
) {
  if (loadingRepositories) {
    return <LoadingSkeleton rows={5} rowHeight="h-16" />;
  }

  if (filteredRepositories.length === 0) {
    return (
      <EmptyState
        size="sm"
        title="No repositories match"
        description="Adjust the filter or connect more repositories in Integrations."
      />
    );
  }

  return filteredRepositories.map((repository) => (
    <RepositoryPickerRow
      key={repository.repository_id}
      isLockedOut={
        selectedProviderId !== null &&
        selectedProviderId !== repository.provider_id
      }
      isSelected={selectedRepositorySlugs.includes(repository.remote_slug)}
      onClick={() => onToggleRepository(repository)}
      repository={repository}
    />
  ));
}
